import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import api from "@/lib/api";
import { levelLabel, LEVEL_FILTERS } from "@/lib/levels";
import { youtubeThumb } from "@/lib/youtube";
import { SKILLS } from "@/lib/skills";
import { Lock, Search, Play } from "lucide-react";

export default function Games() {
  const [games, setGames] = useState([]);
  const [level, setLevel] = useState("");
  const [query, setQuery] = useState("");
  const [videoOnly, setVideoOnly] = useState(false);
  const [skill, setSkill] = useState("");

  useEffect(() => {
    api.get("/games", { params: level ? { level } : {} }).then(({ data }) => setGames(data)).catch(() => {});
  }, [level]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return games.filter((g) => {
      if (videoOnly && !g.video_url) return false;
      if (skill && !(g.skill_sets || []).includes(skill)) return false;
      if (!q) return true;
      return [g.name, g.blurb, ...(g.skill_sets || [])].join(" ").toLowerCase().includes(q);
    });
  }, [games, query, videoOnly, skill]);

  return (
    <div data-testid="games-page">
      <PageHeader eyebrow="Games Library" title="Explore the games"
        subtitle="A public teaser of the method's games. Full descriptions, examples, and ways to play without speaking unlock for enrolled participants." />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="relative mb-6 max-w-md">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#bf6163]" aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search games by name, skill, or keyword…"
            aria-label="Search games"
            data-testid="games-search-input"
            className="w-full h-12 pl-10 pr-4 rounded-full border border-[#dfd8da] bg-white text-[#1a1617] focus:border-[#920e12] focus:ring-2 focus:ring-[#ffdedf] outline-none"
          />
        </div>
        <div className="flex flex-wrap gap-2 mb-4" role="tablist" aria-label="Filter by level">
          {LEVEL_FILTERS.map(([l, label]) => (
            <button
              key={l || "all"}
              onClick={() => setLevel(l)}
              data-testid={`games-filter-${l || "all"}`}
              className={`px-4 py-2 rounded-full text-sm font-bold transition-colors duration-150 ${level === l ? "bg-[#920e12] text-white" : "bg-white border border-[#dfd8da] text-[#545454]"}`}
            >
              {label}
            </button>
          ))}
          <button
            onClick={() => setVideoOnly((v) => !v)}
            aria-pressed={videoOnly}
            data-testid="games-filter-video"
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold transition-colors duration-150 ${videoOnly ? "bg-[#920e12] text-white" : "bg-white border border-[#dfd8da] text-[#545454]"}`}
          >
            <Play size={12} fill="currentColor" aria-hidden="true" /> Has video
          </button>
        </div>
        <div className="flex flex-wrap gap-2 mb-8" aria-label="Filter by skill" data-testid="games-skill-filters">
          {["", ...SKILLS].map((s) => (
            <button key={s || "all"} onClick={() => setSkill(s)} aria-pressed={skill === s}
              data-testid={`games-skill-${s ? s.toLowerCase().replace(/\s+/g, "-") : "all"}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors duration-150 ${skill === s ? "bg-[#1a1617] text-white" : "bg-[#ffdedf] text-[#920e12]"}`}>
              {s || "All skills"}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((g) => (
            <Link key={g.id} to={`/games/${g.id}`} data-testid={`game-card-${g.id}`}
              className="group bg-white rounded-2xl border border-[#dfd8da] overflow-hidden hover:border-[#920e12] transition-colors duration-150">
              {g.video_url && (
                <div className="relative aspect-video bg-[#1a1617] overflow-hidden">
                  <img src={youtubeThumb(g.video_url)} alt="" loading="lazy" aria-hidden="true"
                    className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105" />
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="flex items-center justify-center w-11 h-11 rounded-full bg-[#920e12]/90 text-white shadow-lg">
                      <Play size={18} fill="currentColor" aria-hidden="true" />
                    </span>
                  </span>
                </div>
              )}
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-widest font-extrabold text-[#bf6163]">{levelLabel(g.level)}</span>
                    {g.video_url && (
                      <span data-testid={`game-video-badge-${g.id}`}
                        className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#920e12] text-white">
                        <Play size={10} fill="currentColor" aria-hidden="true" /> Video
                      </span>
                    )}
                  </div>
                  <Lock size={14} className="text-[#bf6163]" aria-label="Full details gated" />
                </div>
                <h2 className="mt-2 text-xl font-bold text-[#1a1617] group-hover:text-[#920e12]">{g.name}</h2>
                <p className="mt-2 text-sm text-[#545454]">{g.blurb}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {g.skill_sets.map((s) => (
                    <span key={s} className="text-[11px] px-2 py-0.5 rounded-full bg-[#ffdedf] text-[#920e12] font-semibold">{s}</span>
                  ))}
                </div>
              </div>
            </Link>
          ))}
        </div>
        {filtered.length === 0 && (
          <p className="text-center text-[#545454]" data-testid="games-empty">
            {query ? `No games match "${query}".` : "No games match these filters yet."}
          </p>
        )}
      </div>
    </div>
  );
}
